import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const ROLES = ["org_admin", "tax_manager", "risk_owner", "control_owner", "consultant", "auditor"];

export default function SettingsPage() {
  const { user, org, updateOrg } = useAuth();
  const [team, setTeam] = useState([]);
  const [invite, setInvite] = useState({ email: "", full_name: "", role: "control_owner" });
  const [orgForm, setOrgForm] = useState({});
  const [profileForm, setProfileForm] = useState({});
  const [openInvite, setOpenInvite] = useState(false);

  useEffect(() => {
    api.get("/team").then(({ data }) => setTeam(data));
    if (org) setOrgForm({ name: org.name, vat_number: org.vat_number, industry: org.industry, white_label_slug: org.white_label_slug, white_label_primary_color: org.white_label_primary_color });
    if (user) setProfileForm({ full_name: user.full_name, phone: user.phone, language: user.language });
  }, [org, user]);

  const saveOrg = async () => {
    try {
      const { data } = await api.put("/organizations/me", orgForm);
      updateOrg(data); toast.success("Organizzazione aggiornata");
    } catch (e) { toast.error("Errore"); }
  };

  const inviteUser = async () => {
    try {
      const { data } = await api.post("/team/invite", invite);
      toast.success(`Invito inviato. Password temporanea: ${data.temporary_password}`);
      setOpenInvite(false); setInvite({ email: "", full_name: "", role: "control_owner" });
      const { data: t } = await api.get("/team"); setTeam(t);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <div className="label-tag">Configurazione</div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900 mt-1">Impostazioni</h1>
      </div>

      <Tabs defaultValue="profile">
        <TabsList className="rounded-sm">
          <TabsTrigger value="profile" className="rounded-sm">Profilo</TabsTrigger>
          <TabsTrigger value="org" className="rounded-sm">Organizzazione</TabsTrigger>
          <TabsTrigger value="team" className="rounded-sm">Team</TabsTrigger>
          <TabsTrigger value="white" className="rounded-sm">White-Label</TabsTrigger>
          <TabsTrigger value="security" className="rounded-sm">Sicurezza</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-4">
          <Card className="card-flat p-6 max-w-xl space-y-4">
            <div><Label className="label-tag">Nome</Label><Input value={profileForm.full_name || ""} onChange={(e) => setProfileForm((f) => ({ ...f, full_name: e.target.value }))} className="rounded-sm mt-1" data-testid="profile-name" /></div>
            <div><Label className="label-tag">Email</Label><Input value={user?.email} disabled className="rounded-sm mt-1" /></div>
            <div><Label className="label-tag">Telefono</Label><Input value={profileForm.phone || ""} onChange={(e) => setProfileForm((f) => ({ ...f, phone: e.target.value }))} className="rounded-sm mt-1" /></div>
          </Card>
        </TabsContent>

        <TabsContent value="org" className="mt-4">
          <Card className="card-flat p-6 max-w-2xl space-y-4">
            <div><Label className="label-tag">Ragione sociale</Label><Input value={orgForm.name || ""} onChange={(e) => setOrgForm((f) => ({ ...f, name: e.target.value }))} className="rounded-sm mt-1" data-testid="org-name" /></div>
            <div><Label className="label-tag">P. IVA</Label><Input value={orgForm.vat_number || ""} onChange={(e) => setOrgForm((f) => ({ ...f, vat_number: e.target.value }))} className="rounded-sm mt-1" /></div>
            <div><Label className="label-tag">Settore</Label><Input value={orgForm.industry || ""} onChange={(e) => setOrgForm((f) => ({ ...f, industry: e.target.value }))} className="rounded-sm mt-1" /></div>
            <Button onClick={saveOrg} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="save-org">Salva</Button>
          </Card>
        </TabsContent>

        <TabsContent value="team" className="mt-4">
          <div className="flex justify-end mb-3">
            <Dialog open={openInvite} onOpenChange={setOpenInvite}>
              <DialogTrigger asChild><Button className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="invite-btn">+ Invita utente</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Invita utente</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <Input placeholder="Email" value={invite.email} onChange={(e) => setInvite((i) => ({ ...i, email: e.target.value }))} className="rounded-sm" data-testid="invite-email" />
                  <Input placeholder="Nome completo" value={invite.full_name} onChange={(e) => setInvite((i) => ({ ...i, full_name: e.target.value }))} className="rounded-sm" />
                  <Select value={invite.role} onValueChange={(v) => setInvite((i) => ({ ...i, role: v }))}>
                    <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <DialogFooter><Button onClick={inviteUser} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="send-invite">Invia</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <Card className="card-flat overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b text-xs uppercase tracking-wider text-slate-600"><tr><th className="py-3 px-3 text-left">Nome</th><th className="py-3 px-3 text-left">Email</th><th className="py-3 px-3 text-left">Ruolo</th><th className="py-3 px-3 text-left">Stato</th></tr></thead>
              <tbody>
                {team.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100">
                    <td className="py-3 px-3 text-slate-900">{u.full_name}</td>
                    <td className="py-3 px-3 text-slate-600">{u.email}</td>
                    <td className="py-3 px-3 text-slate-700">{u.role.replace("_", " ")}</td>
                    <td className="py-3 px-3"><span className={u.is_active ? "text-emerald-700" : "text-slate-400"}>{u.is_active ? "Attivo" : "Disattivato"}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="white" className="mt-4">
          <Card className="card-flat p-6 max-w-2xl space-y-4">
            <p className="text-sm text-slate-600">Personalizza la piattaforma con il tuo brand professionale.</p>
            <div><Label className="label-tag">Slug URL personalizzato</Label><Input value={orgForm.white_label_slug || ""} onChange={(e) => setOrgForm((f) => ({ ...f, white_label_slug: e.target.value }))} placeholder="studio-rossi" className="rounded-sm mt-1" /></div>
            <div><Label className="label-tag">Colore primario</Label><Input type="color" value={orgForm.white_label_primary_color || "#0F172A"} onChange={(e) => setOrgForm((f) => ({ ...f, white_label_primary_color: e.target.value }))} className="rounded-sm mt-1 h-12" /></div>
            <Button onClick={saveOrg} className="rounded-sm bg-slate-900 hover:bg-slate-800">Salva white-label</Button>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="mt-4">
          <Card className="card-flat p-6 max-w-2xl">
            <h3 className="font-display text-lg font-semibold mb-3">Sicurezza</h3>
            <p className="text-sm text-slate-600">Ruolo attuale: <b>{user?.role}</b></p>
            <p className="text-sm text-slate-600 mt-2">Token di sessione: 24h · refresh token: 7 giorni.</p>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
